import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Typography from '@repo/ui/typography'
import { forwardRef, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { ZodType } from 'zod'

import useTranslate from '../../../../lib/hooks/use-translate'
import {
  FriendPlaceEnum,
  friendPlaceEnumMapping
} from '../../../../lib/types/enums/friend/friend-place.enum'
import { FriendInput } from '../../../../lib/types/models/friend/friend-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../../elements/validation-buttons'

export const friendFormSchema: ZodType<FriendInput> = z.object({
  type: createEnumSchema({
    enumType: FriendPlaceEnum,
    fieldName: 'Demande'
  }),
  firstName: createStringSchema('prénom'),
  lastName: createStringSchema('NOM')
})

export type FriendFormRef = {
  submitForm: () => Promise<boolean>
}

type Props = BaseFormProps<FriendInput, true> & {
  className?: string
  containerClassName?: string
  variant?: 'complete' | 'content-only'
  onChange?: (input: FriendInput) => void
}
const FriendForm = forwardRef<FriendFormRef, Props>(
  (
    {
      className,
      containerClassName,
      defaultValues,
      variant = 'complete',
      onChange,
      ...validationButtonProps
    },
    ref
  ) => {
    const { t } = useTranslate()

    const form = useForm<FriendInput>({
      resolver: zodResolver(friendFormSchema),
      defaultValues,
      mode: FORM_MODE
    })

    useImperativeHandle(ref, () => ({
      submitForm: async () => {
        form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))()
        const isValid = await form.trigger()
        return isValid
      }
    }))

    form.watch((value) => {
      if (onChange) onChange(value as FriendInput)
    })

    return (
      <Form {...form}>
        <form
          className={containerClassName}
          onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        >
          <div className={cn('flex flex-col gap-4', className)}>
            <div className="flex gap-3">
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledInput {...field} type="text" label="NOM" required />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledInput {...field} type="text" label="Prénom" required />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div>
              <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
                Demande :
              </Typography>
              <div className="h-1" />
              <div className="flex gap-10">
                {Object.values(FriendPlaceEnum).map((type) => (
                  <FormField
                    key={type}
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={t(friendPlaceEnumMapping[type].label)}
                            checked={field.value === type}
                            onCheck={() => field.onChange(type)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
          {variant === 'complete' && <ValidationButtons {...validationButtonProps} hasBorderTop />}
        </form>
      </Form>
    )
  }
)
export default FriendForm
